/**
 * @sw-package b2b
 */

import Plugin from 'src/plugin-system/plugin.class';
import { showBootstrapToast } from '../../helper/bootstrap-toast.helper';

type QuoteLineItemDeleteConfirmedDetail = {
    context?: {
        removeUrl?: unknown,
    },
};

export default class B2bQuoteDetailPlugin extends Plugin {
    static options = {
        selectors: {
            expirationDate: '.quote-expiration-date',
            status: '[data-status]',
            expiredStatus: '.quote-item-status-badge-expired:not([data-status])',
            declinedButton: '#btn-quote-decline',
            requestChangesButton: '#btn-quote-request-changes',
            checkoutButton: '#btn-quote-checkout',
            manageLineItemsModal: '.quote-manage-line-items-modal',
            hiddenSubmitButton: '[data-hidden-quote-manage-line-items-submit-button]',
            manageLineItemsPlugin: '[data-b2b-quote-manage-line-items-plugin]',
            quoteDetailReloadForm: '[data-b2b-quote-detail-reload-form]',
            quoteDetailTableContainer: '.quote-detail-line-items-table-container',
            quoteDetailTableForm: '.quote-detail-line-items-table-container__form',
            quoteLineItemEditAction: '[data-b2b-quote-line-item-action="edit"]',
            quoteLineItemAddCommentAction: '[data-b2b-quote-line-item-action="comment"]',
            quoteLineItemHistoryAction: '[data-b2b-quote-line-item-action="history"]',
            quoteManageLineItemButton: '.btn-quote-manage-line-items',
        },

        attributes: {
            dataExpiration: 'data-expiration',
        },

        events: {
            historyReadSucceeded: 'b2b-quote-history-read-succeeded',
            quoteDetailReload: 'b2b-quote-detail-reload',
            quoteLineItemDeleteConfirmed: 'b2b-quote-manage-line-item-delete-confirmed',
        },
    };

    declare _quoteDetailReloadForm: HTMLFormElement | null;

    declare _quoteDetailTableForm: HTMLFormElement | null;

    declare _boundHandleClick: (event: Event) => void;

    declare _boundHandleHistoryReadSucceeded: () => void;

    declare _boundHandleReloadQuoteDetail: (event: Event) => void;

    declare _boundHandleQuoteLineItemDeleteConfirmed: (event: Event) => void;

    declare _manageLineItemsModal: HTMLElement | null;

    declare _hiddenSubmitButton: HTMLButtonElement | null;

    declare _quoteManageLineItemButton: HTMLButtonElement | null;

    init(): void {
        this.syncState();
        this._boundHandleClick = this._handleClick.bind(this);
        this._boundHandleHistoryReadSucceeded = this._handleHistoryReadSucceeded.bind(this);
        this._boundHandleReloadQuoteDetail = this._handleReloadQuoteDetail.bind(this);
        this._boundHandleQuoteLineItemDeleteConfirmed = this._handleQuoteLineItemDeleteConfirmed.bind(this);

        this.renderExpiredLabel();
        this._registerEvents();
        this._triggerQuoteDetailLineItemsInitialLoad();
    }

    update(): void {
        this.syncState();
    }

    syncState(): void {
        this._expirationDate = this.el.querySelector(this.options.selectors.expirationDate);
        this._status = this.el.querySelector(this.options.selectors.status);
        this._expiredStatus = this.el.querySelector(this.options.selectors.expiredStatus);
        this._declinedButton = this.el.querySelector(this.options.selectors.declinedButton);
        this._requestChangesButton = this.el.querySelector(this.options.selectors.requestChangesButton);
        this._checkoutButton = this.el.querySelector(this.options.selectors.checkoutButton);
        this._manageLineItemsModal = this.el.querySelector(this.options.selectors.manageLineItemsModal);
        this._quoteDetailReloadForm = this.el.querySelector(this.options.selectors.quoteDetailReloadForm);
        this._quoteDetailTableForm = this.el.querySelector(this.options.selectors.quoteDetailTableForm);
        this._hiddenSubmitButton = this.el.querySelector(this.options.selectors.hiddenSubmitButton);
        this._quoteManageLineItemButton = this.el.querySelector(this.options.selectors.quoteManageLineItemButton);
    }

    renderExpiredLabel(): void {
        const expirationDate = this._expirationDate?.attributes[this.options.attributes.dataExpiration]?.value;
        if (!expirationDate) {
            return;
        }

        const isExpired = new Date(expirationDate).getTime() < Date.now();
        if (isExpired) {
            if (this._expiredStatus) {
                this._expiredStatus.classList.remove('d-none');
                this._status?.classList.add('d-none');
            }

            this._declinedButton?.setAttribute('disabled', 'true');
            this._requestChangesButton?.setAttribute('disabled', 'true');
            this._checkoutButton?.classList.add('disabled');
            return;
        }
    }

    _registerEvents(): void {
        this.el.addEventListener('click', this._boundHandleClick);
        document.addEventListener(this.options.events.historyReadSucceeded, this._boundHandleHistoryReadSucceeded);
        this.el.addEventListener(this.options.events.quoteDetailReload, this._boundHandleReloadQuoteDetail);
        this.el.addEventListener(this.options.events.quoteLineItemDeleteConfirmed, this._boundHandleQuoteLineItemDeleteConfirmed);
    }

    _handleClick(event: Event): void {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const editAction = target.closest<HTMLElement>(this.options.selectors.quoteLineItemEditAction);
        const addCommentAction = target.closest<HTMLElement>(this.options.selectors.quoteLineItemAddCommentAction);
        const historyAction = target.closest<HTMLElement>(this.options.selectors.quoteLineItemHistoryAction);

        if (!editAction && !addCommentAction && !historyAction) {
            return;
        }

        event.preventDefault();

        const lineItemId = editAction?.dataset.lineItemId
            || addCommentAction?.dataset.lineItemId
            || historyAction?.dataset.lineItemId;
        let action: 'edit' | 'comment' | 'history' = 'comment';
        if (editAction) {
            action = 'edit';
        } else if (historyAction) {
            action = 'history';
        }

        this._copyQuoteDetailTableParamsToManageLineItemsForm(lineItemId, action);
        this._quoteManageLineItemButton?.click();
    }

    _copyQuoteDetailTableParamsToManageLineItemsForm(lineItemId: string | undefined, action: 'edit' | 'comment' | 'history'): void {
        if (!this._quoteDetailTableForm || !this._hiddenSubmitButton) {
            return;
        }

        const params = this._readQuoteDetailTableParams();

        this._applyParamsToManageLineItemsForm(params, lineItemId, action);
    }

    _readQuoteDetailTableParams(): URLSearchParams {
        const form = this._quoteDetailTableForm;
        const params = new URLSearchParams();

        if (!form) {
            return params;
        }

        const paramNames = this._getCopiedQuoteDetailTableParamNames();
        const actionUrl = new URL(form.getAttribute('action') || window.location.href, window.location.origin);
        const formData = new FormData(form);

        paramNames.forEach((paramName) => {
            if (actionUrl.searchParams.has(paramName)) {
                params.set(paramName, actionUrl.searchParams.get(paramName) || '');
            }

            const fieldValue = formData.get(paramName);
            if (typeof fieldValue === 'string') {
                params.set(paramName, fieldValue.trim());
            }
        });

        return params;
    }

    _applyParamsToManageLineItemsForm(params: URLSearchParams, lineItemId: string | undefined, action: 'edit' | 'comment' | 'history'): void {
        const hiddenSubmitButton = this._hiddenSubmitButton;

        if (!hiddenSubmitButton) {
            return;
        }

        const formAction = hiddenSubmitButton.dataset.formAction;
        if (!formAction) {
            return;
        }

        const actionUrl = new URL(formAction, window.location.origin);

        this._getCopiedQuoteDetailTableParamNames().forEach((paramName) => {
            const value = params.get(paramName);

            if (value === null || value === '') {
                actionUrl.searchParams.delete(paramName);

                return;
            }

            actionUrl.searchParams.set(paramName, value);
        });

        hiddenSubmitButton.dataset.formAction = `${actionUrl.pathname}${actionUrl.search}${actionUrl.hash}`;

        if (lineItemId && this._manageLineItemsModal) {
            this._manageLineItemsModal.dataset.focusLineItemId = lineItemId;
            this._manageLineItemsModal.dataset.initialAction = action;
        }
    }

    _getCopiedQuoteDetailTableParamNames(): string[] {
        return ['limit', 'p', 'order', 'term'];
    }

    _triggerQuoteDetailLineItemsInitialLoad(): void {
        this._quoteDetailTableForm?.dispatchEvent(new Event('submit', {
            bubbles: true,
            cancelable: true,
        }));
    }

    _handleReloadQuoteDetail(): void {
        this._quoteDetailReloadForm?.dispatchEvent(new Event('submit', {
            bubbles: true,
            cancelable: true,
        }));
    }

    _handleHistoryReadSucceeded(): void {
        this._triggerQuoteDetailLineItemsInitialLoad();
    }

    _handleQuoteLineItemDeleteConfirmed(event: Event): void {
        const detail = (event as CustomEvent<QuoteLineItemDeleteConfirmedDetail>).detail || {};

        void this._submitQuoteLineItemDelete(detail).catch((error) => {
            showBootstrapToast(error);
        });
    }

    async _submitQuoteLineItemDelete(detail: QuoteLineItemDeleteConfirmedDetail): Promise<void> {
        const removeUrl = this._getStringFromUnknown(detail.context?.removeUrl);

        if (!removeUrl) {
            return;
        }

        const response = await fetch(removeUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                'X-Requested-With': 'XMLHttpRequest',
            },
        });

        if (!response.ok) {
            return;
        }

        this._handleReloadQuoteDetail();
    }

    _getStringFromUnknown(value: unknown): string {
        return typeof value === 'string' ? value.trim() : '';
    }
}
